import { Inbox } from '@spike/omnichannel-model';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { FunctionComponent } from 'react';
import { useScopedApiClientWrapper } from '../useScopedApiClientWrapper';
import ChannelCard from './ChannelCard/ChannelCard';
import EmailChannelSection from './EmailChannelSection';
import { textMessagesCardContent } from './textMessagesCard';
import styles from './InboxSettingsDrawer.module.scss';

interface InboxSettingsDrawerProps {
    marketplaceId: number;
    locationName?: string;
    inboxes: Array<Inbox>;
    onClose: () => void;
}

const inboxByType = (inboxes: Array<Inbox>, channelType: Inbox['channelType']): Inbox | undefined =>
    inboxes.find(inbox => inbox.channelType === channelType);

/**
 * Per-location inbox settings. Channels are the location's `Inbox` rows (SMS number, email sender):
 * they live here, next to the conversations they serve, the same way Email Marketing keeps its
 * sender inside Marketing. External accounts (Gmail, Outlook, WhatsApp) are phase 2.
 */
export const InboxSettingsDrawer: FunctionComponent<InboxSettingsDrawerProps> = props => {
    const scopedApiClientWrapper = useScopedApiClientWrapper(props.marketplaceId);

    const textMessages = textMessagesCardContent(inboxByType(props.inboxes, 'sms'));

    return (
        <Drawer onClose={props.onClose}>
            <DrawerHeader title="Inbox settings" />

            <DrawerBody className={styles.body}>
                {props.locationName && <p className={styles.location}>{props.locationName}</p>}

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Channels</h3>
                    <p className={styles.sectionDescription}>
                        Where this location sends and receives client messages from the Inbox.
                    </p>

                    <div className={styles.cards}>
                        <EmailChannelSection
                            inbox={inboxByType(props.inboxes, 'email_ses')}
                            apiClientWrapper={scopedApiClientWrapper}
                        />
                        <ChannelCard
                            icon="/images/settings/integrations/text-messages.svg"
                            title="Text messages"
                            description={textMessages.description}
                            status={textMessages.status}
                            footnote={textMessages.footnote}
                            muted={textMessages.muted}
                        />
                    </div>
                </section>
            </DrawerBody>
        </Drawer>
    );
};

export default InboxSettingsDrawer;
